import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// Predefined time ranges
const timeRanges = [
  { label: '1h', value: '1h' },
  { label: '6h', value: '6h' },
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: 'Custom', value: 'custom' },
];

export default function QuickFilters({
  filters,
  onFilterChange,
  onApply,
  onClear,
  loading,
  timeRange: externalTimeRange,
  onTimeRangeChange,
}) {
  const [internalTimeRange, setInternalTimeRange] = useState('24h');
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Use external time range if provided, otherwise use internal state
  const timeRange = externalTimeRange || internalTimeRange;
  const setTimeRange = (value) => {
    if (onTimeRangeChange) {
      onTimeRangeChange(value);
    } else {
      setInternalTimeRange(value);
    }
  };

  // Calculate time range
  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    if (range === 'custom') {
      setShowCustomDate(true);
      return;
    }

    setShowCustomDate(false);
    const now = new Date();
    let startDate = new Date();

    switch (range) {
      case '1h':
        startDate.setTime(now.getTime() - (60 * 60 * 1000));
        break;
      case '6h':
        startDate.setTime(now.getTime() - (6 * 60 * 60 * 1000));
        break;
      case '24h':
        startDate.setTime(now.getTime() - (24 * 60 * 60 * 1000));
        break;
      case '7d':
        startDate.setTime(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case '30d':
        startDate.setTime(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      default:
        startDate = null;
    }

    onFilterChange('startDate', startDate ? startDate.toISOString() : '');
    onFilterChange('endDate', '');
  };

  // Auto-apply when time range changes
  useEffect(() => {
    if (timeRange !== 'custom' && filters.startDate) {
      const timer = setTimeout(() => {
        onApply();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filters.startDate]);

  // Count active filters (categories array + level + search)
  const activeFilterCount = [
    (filters.categories?.length > 0),
    filters.level,
    filters.search,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {/* Time Range Toggle - bigger buttons */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {timeRanges.map((range) => (
            <button
              key={range.value}
              onClick={() => handleTimeRangeChange(range.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                timeRange === range.value
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-white'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-[150px] max-w-[250px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            placeholder="Search logs..."
            className="w-full pl-9 pr-8 py-2 bg-gray-100 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {filters.search && (
            <button
              onClick={() => onFilterChange('search', '')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Apply */}
        <button
          onClick={onApply}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            loading
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Apply
        </button>

        {/* Clear */}
        {activeFilterCount > 0 && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
          >
            <XMarkIcon className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Custom Date Range */}
      {showCustomDate && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500">From:</span>
          <input
            type="datetime-local"
            value={filters.startDate ? filters.startDate.slice(0, 16) : ''}
            onChange={(e) => onFilterChange('startDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
            className="px-3 py-2 bg-gray-100 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-gray-500">To:</span>
          <input
            type="datetime-local"
            value={filters.endDate ? filters.endDate.slice(0, 16) : ''}
            onChange={(e) => onFilterChange('endDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
            className="px-3 py-2 bg-gray-100 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
    </div>
  );
}
